import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, Stack, styled, Typography } from "@mui/material";
import { ReduxState, useRefresh, useShowContext } from "ra-core";
import ArrowRightIcon from '@/icons/ArrowRight';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import AddIcon from '@mui/icons-material/Add';
import NavigationRefresh from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import { FC, useState } from "react";
import { useSelector } from "react-redux";
import { RecPatch } from "@/dataProvider/tools";
import { GreenJobs, RedJobs, YellowJobs } from "../dashboard/statusBadge";
import { Link as RouterLink } from 'react-router-dom'
import { EncryptionDashboardResponseDto } from "@/gen/api/encrypt";
import ScheduleEncryption from "./ScheduleEncryption";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { SCHEDULE_ENCRYPTION_OBFUSCATION } from "@/constants";


const SlideEncryptionButtonsBoard = styled(Box)(({theme}) => ({
  marginTop: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'normal',
  '& .MuiButton-root': {
    marginBottom: theme.spacing(1.5),
  },
  '& > button.MuiButton-text': {
    paddingTop: theme.spacing(0.25), 
    paddingBottom: theme.spacing(0.25), 
    borderRadius: theme.spacing(0.5),
    transition: 'height 0.5s ease, opacity 0.12s',
    '&.lights-off': {
      height: 0,
      opacity: 0,
      margin: -1,
    },
  },
  '& div.slide-buttons-container': {
    height: 145, 
    position: 'relative', 
    overflowY: 'hidden',
  },
  '& div.slide-board': {
    position: 'relative',
    top: 0,
    transition: 'top 0.5s ease-in-out',
    '&.down': {
      top: -145,
    },
  },
}))


const EncryptionStatusCard: FC = () => {
  const hasWritePermit = useHasPermit([SCHEDULE_ENCRYPTION_OBFUSCATION])
  const { record } = useShowContext<EncryptionDashboardResponseDto & RecPatch>()
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0);
  const refresh = useRefresh()

  const [down, setDown] = useState(false)
  return (
    <Card>
      <CardHeader 
        sx={{ pb: 0 }}
        title={(
          <Typography variant="h6" color="textPrimary">
            Encryption Status
          </Typography>
        )}
        action={(
          <Button
            size="small"
            startIcon={loading ?<CircularProgress size={14} /> : <NavigationRefresh />}
            color="secondary"
            onClick={() => refresh()}
          >
            Refresh
          </Button>)}
      />
      <CardContent>
        <Divider sx={{ mb: 1 }} />
        <Grid container direction="row" sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography
              color="textSecondary"
              variant="overline"
            >
              Encryption Jobs This Week
            </Typography>
            <Stack 
              component="ul"
              direction="column"
              sx={{ pl: 'unset', mt: 2, mb: 1, }}
            >
              <GreenJobs count={record?.encryptionSuccessCount || 0} />
              <YellowJobs count={record?.encryptionWarningCount || 0} />
              <RedJobs count={record?.encryptionFailedCount || 0} />
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <SlideEncryptionButtonsBoard>
              <Box component={Button} onClick={() => setDown(false)} className={!down ? 'lights-off' : undefined} tabIndex={down ? undefined : -1}>
                <KeyboardDoubleArrowUpIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                <Typography variant="caption" color="secondary.main">
                  Back to Encryption
                </Typography>
              </Box>
              <Box className="slide-buttons-container">
                <Stack direction="column" alignItems="normal" className={`slide-board ${down ? 'down' : ''}`}>
                  <Button
                    color="primary"
                    endIcon={<ArrowRightIcon fontSize="small" />}
                    variant="outlined"
                    component={RouterLink}
                    tabIndex={down ? -1 : undefined}
                    to="/encryption/mappings"
                  >
                    Encryption Mappings
                  </Button>
                  <Button
                    color="primary"
                    endIcon={<ArrowRightIcon fontSize="small" />}
                    variant="outlined"
                    component={RouterLink}
                    tabIndex={down ? -1 : undefined}
                    to="/encryption/list"
                  >
                    View Encryption Jobs
                  </Button>
                  <ScheduleEncryption 
                    button={
                      <Button
                        disabled={!hasWritePermit}
                        color="primary"
                        startIcon={<AddIcon fontSize="small" />}
                        tabIndex={down ? -1 : undefined}
                        variant="outlined"
                        sx={{ visibility: !hasWritePermit ? 'hidden' : undefined }}
                      >
                        Schedule Encryption Job
                      </Button>
                    }
                  />
                  <Button
                    color="primary"
                    endIcon={<ArrowRightIcon fontSize="small" />}
                    variant="outlined"
                    component={RouterLink}
                    tabIndex={down ? undefined : -1}
                    to="/encryption/obfs/mappings"
                  >
                    Obfuscation Mappings
                  </Button>
                  <Button
                    color="primary"
                    endIcon={<ArrowRightIcon fontSize="small" />}
                    variant="outlined"
                    component={RouterLink}
                    tabIndex={down ? undefined : -1}
                    to="/encryption/obfs/list"
                  >
                    View Obfuscation Jobs
                  </Button>
                  <ScheduleEncryption 
                    button={
                      <Button
                        disabled={!hasWritePermit}
                        color="primary"
                        startIcon={<AddIcon fontSize="small" />}
                        tabIndex={down ? undefined : -1}
                        variant="outlined"
                        sx={{ visibility: !hasWritePermit ? 'hidden' : undefined }}
                      >
                        Schedule Obfuscation Job
                      </Button>
                    }
                    isObfuscation={true}
                  />
                </Stack>
              </Box>
              <Box component={Button} onClick={() => setDown(true)} className={down ? 'lights-off' : undefined} tabIndex={down ? -1 : undefined}>
                <KeyboardDoubleArrowDownIcon sx={{ width: 16, height: 16, mr: 0.5 }} />
                <Typography variant="caption" color="secondary.main">
                  More on Obfuscation
                </Typography>
              </Box>
              {/* <Button
                color="primary"
                endIcon={<ArrowRightIcon fontSize="small" />}
                variant="outlined"
                component={RouterLink}
                to="/encryption/custom-rules"
              >
                View Custom Rules
              </Button> */}
            </SlideEncryptionButtonsBoard>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default EncryptionStatusCard;
